import { useUpdateResolutionReasonMutation } from '@/store/api/services/operations/resolutionReasons';
import type { ResolutionReason } from '@/types/serviceTicket';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

interface EditResolutionReasonProps extends ModalChildrenProps {
  props: {
    resolutionReason: ResolutionReason;
  } | null;
}

const EditResolutionReasonSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  is_success_reason: z.boolean(),
  description: z.string().min(1, 'Description is required'),
  is_active: z.boolean(),
});

type EditResolutionReasonSchemaType = z.infer<typeof EditResolutionReasonSchema>;

const EditResolutionReason = ({ hide, props }: EditResolutionReasonProps) => {
  const resolutionReason = props?.resolutionReason;

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EditResolutionReasonSchemaType>({
    resolver: zodResolver(EditResolutionReasonSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: resolutionReason?.name || '',
      is_success_reason: resolutionReason?.is_success_reason || false,
      description: resolutionReason?.description || '',
      is_active: resolutionReason?.is_active ?? true,
    },
  });

  const [updateResolutionReason] = useUpdateResolutionReasonMutation();

  const onSubmit = async (payload: EditResolutionReasonSchemaType) => {
    if (!resolutionReason?.id) {
      toast.error('Resolution reason ID is missing');
      return;
    }

    try {
      await updateResolutionReason({ id: resolutionReason.id, ...payload }).unwrap();
      toast.success('Resolution reason updated successfully');
      hide('edit-resolution-reason');
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message ||
        error?.data?.message ||
        error?.message ||
        'Failed to update resolution reason';
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={2}>
          <TextField
            label="Name *"
            fullWidth
            error={!!errors.name}
            helperText={errors?.name?.message}
            size="small"
            {...register('name')}
          />

          <Controller
            name="is_success_reason"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Is Success Reason"
              />
            )}
          />

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Active"
              />
            )}
          />

          <Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Description *
            </Typography>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <>
                  <TextareaAutosize
                    {...field}
                    minRows={4}
                    maxRows={8}
                    placeholder="Provide a detailed description of the resolution reason"
                    style={{
                      width: '100%',
                      resize: 'vertical',
                      padding: '12px 14px',
                      border: errors.description
                        ? '1px solid #d32f2f'
                        : '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      lineHeight: '1.5',
                      backgroundColor: 'transparent',
                      outline: 'none',
                      cursor: 'text',
                    }}
                  />
                  {errors.description && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.description.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide('edit-resolution-reason')}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isSubmitting ? 'Updating...' : 'Update Resolution Reason'}
        </Button>
      </DialogActions>
    </>
  );
};

export default EditResolutionReason;
